import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Divider, 
  Alert, 
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  Chip
} from '@mui/material';
import { Add, Delete, Save, AccessTime, CalendarToday, LocationOn, Business, Build } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useSiteEquipment } from '../../hooks/useSiteEquipment';
import { useAuth } from '../../hooks/useAuth';
import UserSearchAutocomplete from '../../components/UserSearchAutocomplete';
import SafetyMeasuresManager from '../../components/SafetyMeasures/SafetyMeasuresManager';
import permitService from '../../services/permitService';
import { SiteRole } from '../../types';

interface PermitComprehensiveFormProps {
  permitType?: string;
  highRisk?: string[];
}

interface EquipmentItem {
  id: string;
  site_equipment_id?: number;
  equipment_name: string;
  equipment_number: string;
  equipment_location: string;
  work_description: string;
  // Add new fields for better integration
  site_equipment_details?: any;
}

interface SafetyMeasure {
  id: string;
  measure_number: number;
  measure_type: 'iasm' | 'rasm' | 'general';
  description: string;
  isolation_method_id?: number;
  energy_source_id?: number;
  verification_required: boolean;
  verification_method: string;
  estimated_time_minutes: number;
  tools_required: string;
  ppe_required: string;
  competency_required: string;
  can_be_removed_for_testing: boolean;
  affected_equipment_codes?: string[];
  isolation_rationale?: string;
  is_auto_generated?: boolean;
}



const steps = [
  'Basic Information & Equipment',
  'Safety Measures',
  'Review & Submit'
];

const PermitComprehensiveForm: React.FC<PermitComprehensiveFormProps> = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { permitType?: string; highRisk?: string[] } | undefined;
  
  // Stable references using useMemo
  const permitType = useMemo(() => state?.permitType || props.permitType || 'access', [state?.permitType, props.permitType]);
  const highRisk = useMemo(() => state?.highRisk || props.highRisk || [], [state?.highRisk, props.highRisk]);
  
  const [activeStep, setActiveStep] = useState(0);
  
  // Site/Equipment selection hook
  const siteEquipment = useSiteEquipment();
  const { user, permissions } = useAuth();
  const [form, setForm] = useState({
    // Basic Information
    work_description: '',
    work_location: '',
    recipient_id: '',
    supervisor_id: '',
    requested_start_time: '',
    requested_end_time: '',
    isolation_required: false,
    risk_assessment_completed: false,
    
    // Access/Test specific fields
    equipment_removed_from_service: '',
    issuer_applied_safety_measures: '',
    energization_checklist: '',
    boundary_definition: '',
    
    // Additional fields
    hazard_identification: '',
    emergency_contact_info: '',
    permit_conditions: ''
  });

  const [equipment, setEquipment] = useState<EquipmentItem[]>([]);
  const [safetyMeasures, setSafetyMeasures] = useState<SafetyMeasure[]>([]);

  // Filter sites based on user permissions (show only sites where user can issue permits)
  const availableSites = useMemo(() => {
    if (!user?.site_roles) {
      return siteEquipment.sites;
    }
    
    // Filter sites where user has issuer role
    const issuerSites = user.site_roles
      .filter((role: SiteRole) => role.role === 'issuer' && role.is_active)
      .map((role: SiteRole) => role.site_id);
    
    return siteEquipment.sites.filter(site => issuerSites.includes(site.id));
  }, [siteEquipment.sites, user]);

  // Set default end time to end of day when component loads
  useEffect(() => {
    if (!form.requested_end_time) {
      const endOfDay = new Date();
      endOfDay.setHours(23, 59, 59, 999);
      const endTimeString = endOfDay.toISOString().slice(0, 16);
      setForm(prevForm => ({
        ...prevForm,
        requested_end_time: endTimeString
      }));
    }
  }, [form.requested_end_time]);

  // Prepopulate form when equipment/task selection changes
  useEffect(() => {
    if (siteEquipment.prepopulateData) {
      const data = siteEquipment.prepopulateData;
      
      // Update form with suggested data
      setForm(prevForm => ({
        ...prevForm,
        work_description: data.suggested_work_description || prevForm.work_description,
        work_location: data.equipment.full_location || prevForm.work_location,
        isolation_required: data.suggested_isolation_required,
        // Only update permit type if not already set by wizard
        ...(permitType === 'access' ? {} : { permit_type: data.suggested_permit_type })
      }));

      // Prepopulate safety measures
      if (data.suggested_safety_measures.length > 0) {
        const newSafetyMeasures = data.suggested_safety_measures.map((measure: any, index: number) => ({
          id: `suggested_${Date.now()}_${index}`,
          measure_number: index + 1,
          measure_type: measure.measure_type || 'iasm',
          description: measure.description || '',
          isolation_method_id: measure.isolation_method_id,
          energy_source_id: measure.energy_source_id,
          verification_required: measure.verification_required || false,
          verification_method: measure.verification_method || '',
          estimated_time_minutes: measure.estimated_time_minutes || 0,
          tools_required: measure.tools_required || '',
          ppe_required: measure.ppe_required || '',
          competency_required: measure.competency_required || '',
          can_be_removed_for_testing: measure.can_be_removed_for_testing || false,
          affected_equipment_codes: measure.affected_equipment_codes || [],
          isolation_rationale: measure.isolation_rationale || '',
          is_auto_generated: true
        }));
        setSafetyMeasures(newSafetyMeasures);
      }

      // Prepopulate equipment if not already added
      if (equipment.length === 0) {
        const newEquipment: EquipmentItem = {
          id: `eq_${Date.now()}`,
          equipment_name: data.equipment.name,
          equipment_number: data.equipment.equipment_number,
          equipment_location: data.equipment.full_location || `${data.equipment.site_name} - ${data.equipment.name}`,
          work_description: data.suggested_work_description || 'Work to be performed'
        };
        setEquipment([newEquipment]);
      }
    }
  }, [siteEquipment.prepopulateData, permitType, equipment.length]);

  // Auto-populate work location when equipment is selected (even without task)
  useEffect(() => {
    if (siteEquipment.selectedEquipment && siteEquipment.equipment.length > 0) {
      const selectedEquipmentData = siteEquipment.equipment.find(eq => eq.id === siteEquipment.selectedEquipment);
      if (selectedEquipmentData && selectedEquipmentData.full_location) {
        setForm(prevForm => ({
          ...prevForm,
          work_location: selectedEquipmentData.full_location || `${selectedEquipmentData.site_name} - ${selectedEquipmentData.name}`
        }));
      }
    }
  }, [siteEquipment.selectedEquipment, siteEquipment.equipment]);



  // Stable form change handlers using useCallback
  const handleFormChange = useCallback((field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm(prevForm => ({
      ...prevForm,
      [field]: value
    }));
  }, []);

  const handleSelectChange = useCallback((field: string) => (event: any) => {
    setForm(prevForm => ({
      ...prevForm,
      [field]: event.target.value
    }));
  }, []);

  // Date/time helper functions
  const setStartTimeToNow = useCallback(() => {
    const now = new Date();
    const nowString = now.toISOString().slice(0, 16);
    setForm(prevForm => ({
      ...prevForm,
      requested_start_time: nowString
    }));
  }, []);

  const addDayToEndTime = useCallback(() => {
    const startTime = form.requested_start_time ? new Date(form.requested_start_time) : new Date();
    const endTime = new Date(startTime);
    endTime.setDate(endTime.getDate() + 1);
    endTime.setHours(23, 59, 59, 999); // End of day
    const endTimeString = endTime.toISOString().slice(0, 16);
    setForm(prevForm => ({
      ...prevForm,
      requested_end_time: endTimeString
    }));
  }, [form.requested_start_time]);

  const addWeekToEndTime = useCallback(() => {
    const startTime = form.requested_start_time ? new Date(form.requested_start_time) : new Date();
    const endTime = new Date(startTime);
    endTime.setDate(endTime.getDate() + 7);
    endTime.setHours(23, 59, 59, 999); // End of day
    const endTimeString = endTime.toISOString().slice(0, 16);
    setForm(prevForm => ({
      ...prevForm,
      requested_end_time: endTimeString
    }));
  }, [form.requested_start_time]);

  // Equipment management
  const addEquipment = useCallback(() => {
    const newEquipment: EquipmentItem = {
      id: Date.now().toString(),
      site_equipment_id: undefined,
      equipment_name: '',
      equipment_number: '',
      equipment_location: '',
      work_description: ''
    };
    setEquipment(prev => [...prev, newEquipment]);
  }, []);

  const removeEquipment = useCallback((id: string) => {
    setEquipment(prev => {
      const newEquipment = prev.filter(eq => eq.id !== id);
      // Update work location when equipment is removed
      updateWorkLocationFromEquipment(newEquipment);
      return newEquipment;
    });
  }, []);

  const updateEquipment = useCallback((id: string, field: string, value: any) => {
    setEquipment(prev => {
      const newEquipment = prev.map(eq => {
        if (eq.id === id) {
          const updated = { ...eq, [field]: value };
          
          // If selecting site equipment, auto-populate fields
          if (field === 'site_equipment_id' && value) {
            const selectedEquipment = siteEquipment.equipment.find(e => e.id === value);
            if (selectedEquipment) {
              updated.equipment_name = selectedEquipment.name;
              updated.equipment_number = selectedEquipment.equipment_number || selectedEquipment.equipment_id || '';
              updated.equipment_location = selectedEquipment.full_location || selectedEquipment.site_name;
              updated.site_equipment_details = selectedEquipment;
            }
          }
          
          return updated;
        }
        return eq;
      });
      
      // Update work location when equipment changes
      if (field === 'site_equipment_id') {
        updateWorkLocationFromEquipment(newEquipment);
      }
      
      return newEquipment;
    });
  }, [siteEquipment.equipment]);

  // Helper function to update work location based on equipment
  const updateWorkLocationFromEquipment = useCallback((equipmentList: EquipmentItem[]) => {
    const locations = equipmentList
      .map(eq => eq.equipment_location)
      .filter(loc => loc && loc.trim())
      .filter((loc, index, arr) => arr.indexOf(loc) === index); // Remove duplicates
    
    if (locations.length > 0) {
      setForm(prevForm => ({
        ...prevForm,
        work_location: locations.join(', ')
      }));
    }
  }, []);





  // API mutation - use permitService with CSRF token handling
  const createPermit = async (data: any) => {
    return await permitService.createPermit(data);
  };

  const mutation = useMutation({
    mutationFn: createPermit,
    onSuccess: (data) => {
      navigate(`/permits/${data.id}`);
    },
  });

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!form.work_description || !form.work_location || !form.recipient_id) {
      alert('Please fill in all required fields');
      return;
    }
    
    if (!siteEquipment.selectedSite) {
      alert('Please select a site for this permit');
      return;
    }

    // Prepare payload
    const payload = {
      work_description: form.work_description,
      work_location: form.work_location,
      recipient_id: parseInt(form.recipient_id),
      supervisor_id: form.supervisor_id ? parseInt(form.supervisor_id) : undefined,
      requested_start_time: form.requested_start_time,
      requested_end_time: form.requested_end_time,
      permit_type: permitType,
      isolation_required: form.isolation_required,
      risk_assessment_completed: form.risk_assessment_completed,
      equipment_removed_from_service: form.equipment_removed_from_service,
      issuer_applied_safety_measures: form.issuer_applied_safety_measures,
      high_risk: highRisk,
      site_id: siteEquipment.selectedSite, // Include selected site
      
      // Related data - include site_equipment_id for new workflow
      equipment_data: equipment.map(eq => ({
        site_equipment_id: eq.site_equipment_id || null,
        equipment_name: eq.equipment_name,
        equipment_number: eq.equipment_number,
        equipment_location: eq.equipment_location,
        work_description: eq.work_description
      })),
      safety_measures_data: safetyMeasures
    };

    mutation.mutate(payload);
  }, [form, permitType, highRisk, equipment, safetyMeasures, mutation]);

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Permit Information
              </Typography>
            </Grid>
            
            {/* Site/Equipment Selection */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Site & Equipment Selection
                </Typography>
                <Typography variant="body2">
                  <strong>New Workflow:</strong> You can now select equipment directly after choosing a site. The permit location will be automatically populated based on the equipment's location. 
                  This streamlined process reduces manual data entry and improves accuracy.
                </Typography>
              </Alert>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Site</InputLabel>
                <Select
                  value={siteEquipment.selectedSite || ''}
                  label="Site"
                  onChange={(e) => siteEquipment.handleSiteChange(e.target.value ? Number(e.target.value) : null)}
                  startAdornment={<Business sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="">
                    <em>Select Site</em>
                  </MenuItem>
                  {availableSites.map(site => (
                    <MenuItem key={site.id} value={site.id}>
                      {site.code} - {site.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Location selection removed - automatically determined from equipment */}
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={!siteEquipment.selectedSite}>
                <InputLabel>Equipment</InputLabel>
                <Select
                  value={siteEquipment.selectedEquipment || ''}
                  label="Equipment"
                  onChange={(e) => siteEquipment.handleEquipmentChange(e.target.value ? Number(e.target.value) : null)}
                  startAdornment={<Build sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="">
                    <em>Select Equipment</em>
                  </MenuItem>
                  {siteEquipment.equipment.map(eq => (
                    <MenuItem key={eq.id} value={eq.id}>
                      {eq.equipment_number || eq.equipment_id || eq.kks_code} - {eq.name}
                      <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        ({eq.site_code})
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={!siteEquipment.selectedEquipment || siteEquipment.standardTasks.length === 0}>
                <InputLabel>Standard Task (Optional)</InputLabel>
                <Select
                  value={siteEquipment.selectedTask || ''}
                  label="Standard Task (Optional)"
                  onChange={(e) => siteEquipment.handleTaskChange(e.target.value ? Number(e.target.value) : null)}
                >
                  <MenuItem value="">
                    <em>Custom Work (No Standard Task)</em>
                  </MenuItem>
                  {siteEquipment.standardTasks.map(task => (
                    <MenuItem key={task.id} value={task.id}>
                      {task.task_name} ({task.task_type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {siteEquipment.loading && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="center" py={2}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2">Loading equipment data...</Typography>
                </Box>
              </Grid>
            )}
            
            {siteEquipment.error && (
              <Grid item xs={12}>
                <Alert severity="error">{siteEquipment.error}</Alert>
              </Grid>
            )}
            
            {siteEquipment.prepopulateData && (
              <Grid item xs={12}>
                <Alert severity="success">
                  <Typography variant="body2">
                    ✓ Equipment data loaded! Form has been pre-populated with suggested values based on {siteEquipment.prepopulateData.equipment.equipment_number}.
                    {siteEquipment.prepopulateData.selected_task && ` Standard task: ${siteEquipment.prepopulateData.selected_task.task_name}.`}
                  </Typography>
                </Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Work Description"
                value={form.work_description}
                onChange={handleFormChange('work_description')}
                fullWidth
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Work Location"
                value={form.work_location}
                onChange={handleFormChange('work_location')}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <UserSearchAutocomplete
                siteId={siteEquipment.selectedSite}
                roleFilter="recipients"
                value={form.recipient_id ? parseInt(form.recipient_id) : null}
                onChange={(userId: number | null) => setForm(prevForm => ({ ...prevForm, recipient_id: userId ? userId.toString() : '' }))}
                label="Recipient *"
                placeholder="Search for permit recipient..."
                required
                permitType={permitType}
                helperText="Select a user who can receive this permit type"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <UserSearchAutocomplete
                siteId={siteEquipment.selectedSite}
                roleFilter="supervisors"
                value={form.supervisor_id ? parseInt(form.supervisor_id) : null}
                onChange={(userId: number | null) => setForm(prevForm => ({ ...prevForm, supervisor_id: userId ? userId.toString() : '' }))}
                label="Supervisor"
                placeholder="Search for supervisor..."
                helperText="Optional: Select a supervisor for this work"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Time"
                type="datetime-local"
                value={form.requested_start_time}
                onChange={handleFormChange('requested_start_time')}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={setStartTimeToNow}
                  startIcon={<AccessTime />}
                >
                  Now
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="End Time"
                type="datetime-local"
                value={form.requested_end_time}
                onChange={handleFormChange('requested_end_time')}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={addDayToEndTime}
                  startIcon={<CalendarToday />}
                >
                  +1 Day
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={addWeekToEndTime}
                  startIcon={<CalendarToday />}
                >
                  +1 Week
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.isolation_required}
                      onChange={handleFormChange('isolation_required')}
                    />
                  }
                  label="Isolation Required"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.risk_assessment_completed}
                      onChange={handleFormChange('risk_assessment_completed')}
                    />
                  }
                  label="Risk Assessment Completed"
                />
              </FormGroup>
            </Grid>
            {permitType === 'access' && (
              <Grid item xs={12}>
                <TextField
                  label="Boundary Definition and Signage"
                  value={form.boundary_definition}
                  onChange={handleFormChange('boundary_definition')}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
            )}
            {permitType === 'test' && (
              <Grid item xs={12}>
                <TextField
                  label="Energization Checklist"
                  value={form.energization_checklist}
                  onChange={handleFormChange('energization_checklist')}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
            )}

            {/* Equipment Selection Section */}
            <Grid item xs={12}>
              <Box mt={4}>
                <Typography variant="h6" gutterBottom>
                  <Build sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Equipment Selection
                </Typography>
                
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Select all equipment that will be worked on for this permit. Location information will be automatically populated.
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={addEquipment}
                    startIcon={<Add />}
                  >
                    Add Equipment
                  </Button>
                </Box>
                
                {equipment.length === 0 ? (
                  <Alert severity="info">No equipment added yet. Click "Add Equipment" to begin.</Alert>
                ) : (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Select Equipment</TableCell>
                          <TableCell>Equipment Details</TableCell>
                          <TableCell>Work Description</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {equipment.map((eq) => (
                          <TableRow key={eq.id}>
                            <TableCell sx={{ minWidth: 250 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Select Equipment</InputLabel>
                                <Select
                                  value={eq.site_equipment_id || ''}
                                  label="Select Equipment"
                                  onChange={(e) => updateEquipment(eq.id, 'site_equipment_id', e.target.value ? Number(e.target.value) : undefined)}
                                  disabled={!siteEquipment.selectedSite}
                                >
                                  <MenuItem value="">
                                    <em>Choose equipment...</em>
                                  </MenuItem>
                                  {siteEquipment.equipment.map(equipment => (
                                    <MenuItem key={equipment.id} value={equipment.id}>
                                      <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                          {equipment.equipment_number || equipment.equipment_id}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {equipment.name} - {equipment.full_location}
                                        </Typography>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {eq.equipment_name || 'Not selected'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ID: {eq.equipment_number || 'N/A'}
                                </Typography>
                                <br />
                                <Typography variant="caption" color="text.secondary">
                                  Location: {eq.equipment_location || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={eq.work_description}
                                onChange={(e) => updateEquipment(eq.id, 'work_description', e.target.value)}
                                placeholder="Describe the work to be performed..."
                                multiline
                                rows={2}
                                size="small"
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                onClick={() => removeEquipment(eq.id)}
                                color="error"
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Safety Measures & Isolation Analysis
            </Typography>
            
            <SafetyMeasuresManager
              equipmentIds={equipment
                .filter(eq => eq.site_equipment_id)
                .map(eq => eq.site_equipment_id as number)
              }
              safetyMeasures={safetyMeasures}
              onSafetyMeasuresChange={setSafetyMeasures}
              disabled={false}
            />
            
            {equipment.length === 0 || equipment.every(eq => !eq.site_equipment_id) ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  No equipment selected from the site database. Please go back to the previous step and select equipment 
                  from the site database to enable intelligent isolation analysis and safety measure generation.
                </Typography>
              </Alert>
            ) : null}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review and Submit
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Please review all information before submitting. This will create a {permitType.toUpperCase()} permit.
            </Alert>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Summary</Typography>
                    <Typography variant="body2">Type: {permitType.toUpperCase()}</Typography>
                    <Typography variant="body2">Location: {form.work_location}</Typography>
                    <Typography variant="body2">Equipment Count: {equipment.length}</Typography>
                    <Typography variant="body2">Safety Measures: {safetyMeasures.length}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              {highRisk.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>High-Risk Activities</Typography>
                      {highRisk.map((hr, index) => (
                        <Typography key={index} variant="body2">• {hr}</Typography>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  const handleNext = useCallback(() => {
    setActiveStep((prev) => prev + 1);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  return (
    <Box maxWidth={1200} mx="auto" mt={4}>
      <Typography variant="h4" color="primary" gutterBottom>
        Create {permitType.charAt(0).toUpperCase() + permitType.slice(1)} Permit
      </Typography>
      
      <Card>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <form onSubmit={handleSubmit}>
            <Box minHeight={400} mb={3}>
              {renderStepContent(activeStep)}
            </Box>

            {mutation.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {(mutation.error as Error)?.message || 'Error creating permit'}
              </Alert>
            )}

            {mutation.isPending && (
              <Box display="flex" justifyContent="center" mb={2}>
                <CircularProgress />
              </Box>
            )}

            <Box display="flex" justifyContent="space-between">
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>
              
              {activeStep === steps.length - 1 ? (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={mutation.isPending}
                  startIcon={<Save />}
                >
                  {mutation.isPending ? 'Creating...' : 'Create Permit'}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  variant="contained"
                  color="primary"
                >
                  Next
                </Button>
              )}
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PermitComprehensiveForm; 